// src/App.tsx
import React, { useState, useEffect, useRef } from 'react';
import Landing from './components/Landing';
import DrainageEditor from './components/DrainageEditor';

export const AUTH_TOKEN_KEY = 'civeng_bh_auth_token_v1';
export const AUTH_LABEL_KEY = 'civeng_bh_auth_label_v1';

function getRouteFromLocation(): 'drainage' | 'landing' {
  const clean = window.location.pathname.replace(/\/+$/, '');
  if (clean === '/yls-drainage' || clean === '/drainageEditor') {
    return 'drainage';
  }
  const params = new URLSearchParams(window.location.search);
  if (params.get('tool') === 'drainage') {
    return 'drainage';
  }
  return 'landing';
}

export default function App() {
  const [route, setRoute] = useState<'drainage' | 'landing'>(() =>
    getRouteFromLocation()
  );

  // Workspace Auth State
  const [authToken, setAuthToken] = useState<string | null>(() =>
    localStorage.getItem(AUTH_TOKEN_KEY)
  );
  const [userLabel, setUserLabel] = useState<string | null>(() =>
    localStorage.getItem(AUTH_LABEL_KEY)
  );

  // Soft-Lock Modal State
  const [authModalOpen, setAuthModalOpen] = useState<boolean>(false);
  const [authReason, setAuthReason] = useState<string>(
    'Enter the Chartered Workspace password to unlock AI synthesis, PDF manuals, and engineering calculators.'
  );
  const [passwordInput, setPasswordInput] = useState<string>('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [authSubmitting, setAuthSubmitting] = useState<boolean>(false);

  // Stores the action the user tried to perform right before the Soft Lock modal popped up
  const pendingActionRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const onPopState = () => {
      setRoute(getRouteFromLocation());
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Verify existing token on mount
  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;

    fetch('/api/kb/auth/verify', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Expired');
        return res.json();
      })
      .then((data) => {
        setAuthToken(token);
        setUserLabel(data.user_label || 'Chartered Engineer');
      })
      .catch(() => {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(AUTH_LABEL_KEY);
        setAuthToken(null);
        setUserLabel(null);
      });
  }, []);

  // If user visits /yls-drainage directly via URL while locked, redirect to Landing & pop modal
  useEffect(() => {
    if (route === 'drainage' && !authToken) {
      const target = window.location.pathname + window.location.search; // keep ?artifact=
      window.history.replaceState({}, '', '/');
      setRoute('landing');
      pendingActionRef.current = () => {
        window.history.pushState({}, '', target);
        setRoute('drainage');
      };
      setAuthReason('Unlock Chartered Workspace to launch YLS Surface Drainage.');
      setAuthModalOpen(true);
    }
  }, [route, authToken]);

  useEffect(() => {
    if (route === 'drainage') {
      document.title = 'YLS Surface Drainage | CivEng Cloud';
    } else {
      document.title = 'CivEng Cloud | Autonomous 3-Tier Engineering Suite';
    }
  }, [route]);

  const navigateTo = (path: string) => {
    window.history.pushState({}, '', path);
    setRoute(getRouteFromLocation());
  };

  const openAuthModal = (reason?: string, onUnlocked?: () => void) => {
    if (reason) setAuthReason(reason);
    else {
      setAuthReason(
        'Enter the Chartered Workspace password to unlock AI synthesis, PDF manuals, and engineering calculators.'
      );
    }
    pendingActionRef.current = onUnlocked || null;
    setPasswordInput('');
    setAuthError(null);
    setAuthModalOpen(true);
  };

  const handleSignOut = () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_LABEL_KEY);
    setAuthToken(null);
    setUserLabel(null);
    pendingActionRef.current = null;
    if (route === 'drainage') {
      navigateTo('/');
    }
  };

  const handleEnter = (selectedTool: string) => {
    // Borehole service handles its own login endpoint independently
    if (selectedTool === 'borehole' || selectedTool === 'boreholes') {
      window.location.assign('/boreholes/');
      return;
    }

    if (selectedTool === 'drainage') {
      if (!authToken) {
        openAuthModal(
          'Unlock Chartered Workspace to launch the YLS Surface Drainage Calculator.',
          () => navigateTo('/yls-drainage')
        );
        return;
      }
      navigateTo('/yls-drainage');
      return;
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordInput.trim() || authSubmitting) return;

    setAuthSubmitting(true);
    setAuthError(null);

    try {
      const res = await fetch('/api/kb/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: passwordInput.trim() }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Invalid workspace password.');
      }

      const data = await res.json();
      const token = data.token;
      const label = data.user_label || 'Chartered Engineer';

      localStorage.setItem(AUTH_TOKEN_KEY, token);
      localStorage.setItem(AUTH_LABEL_KEY, label);
      setAuthToken(token);
      setUserLabel(label);
      setAuthModalOpen(false);
      setPasswordInput('');

      // Resume whatever action the user was trying to do
      if (pendingActionRef.current) {
        const callback = pendingActionRef.current;
        pendingActionRef.current = null;
        setTimeout(() => callback(), 50);
      }
    } catch (err: any) {
      setAuthError(err?.message || 'Verification failed.');
    } finally {
      setAuthSubmitting(false);
    }
  };

  return (
    <>
      {route === 'drainage' && authToken ? (
        <DrainageEditor onBack={() => navigateTo('/')} />
      ) : (
        <Landing
          onEnter={handleEnter}
          onSelectModule={handleEnter}
          userEmail={userLabel}
          onOpenAuth={openAuthModal}
          onSignOut={handleSignOut}
        />
      )}

      {/* Soft-Lock Password Modal */}
      {authModalOpen && (
        <div
          onClick={() => setAuthModalOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(6, 7, 11, 0.78)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, sans-serif',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '400px',
              backgroundColor: '#13151d',
              border: '1px solid #262a3b',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.6)',
              color: '#f8fafc',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '14px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #6366f1, #06b6d4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '13px',
                    color: '#fff',
                  }}
                >
                  🔒
                </div>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 700 }}>
                    Chartered Workspace Lock
                  </div>
                  <div style={{ fontSize: '11.5px', color: '#94a3b8' }}>
                    Argon2id Protected Environment
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setAuthModalOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#64748b',
                  fontSize: '16px',
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>

            <p
              style={{
                fontSize: '12.5px',
                lineHeight: 1.5,
                color: '#cbd5e1',
                margin: '0 0 16px 0',
              }}
            >
              {authReason}
            </p>

            <form onSubmit={handleLoginSubmit}>
              <input
                type="password"
                autoFocus
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="Enter workspace password..."
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  backgroundColor: '#0b0b0e',
                  border: authError ? '1px solid #ef4444' : '1px solid #2d3142',
                  color: '#f8fafc',
                  fontSize: '13.5px',
                  outline: 'none',
                  marginBottom: authError ? '8px' : '14px',
                }}
              />

              {authError && (
                <div
                  style={{
                    fontSize: '12px',
                    color: '#f87171',
                    marginBottom: '12px',
                  }}
                >
                  {authError}
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '8px',
                }}
              >
                <button
                  type="button"
                  onClick={() => setAuthModalOpen(false)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    backgroundColor: '#1e212d',
                    border: '1px solid #2d3142',
                    color: '#cbd5e1',
                    fontSize: '12.5px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!passwordInput.trim() || authSubmitting}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                    border: 'none',
                    color: '#fff',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    cursor: passwordInput.trim() ? 'pointer' : 'default',
                    opacity: passwordInput.trim() && !authSubmitting ? 1 : 0.6,
                  }}
                >
                  {authSubmitting ? 'Verifying...' : 'Unlock Workspace'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}